import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Trophy, 
  Calendar, 
  MapPin, 
  Users, 
  Star, 
  ArrowLeft,
  Clock,
  CheckCircle,
  ExternalLink
} from 'lucide-react';
import { tournamentService } from '../services/tournamentService';
import type { Tournament, TournamentStage } from '@/core/types/tournament';

const TournamentDetailPage: React.FC = () => {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [stages, setStages] = useState<TournamentStage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tournamentId) {
      loadTournamentData(tournamentId);
    }
  }, [tournamentId]);

  const loadTournamentData = async (id: string) => {
    try {
      const [tournamentData, stagesData] = await Promise.all([
        tournamentService.getTournamentById(id),
        tournamentService.getStagesByTournament(id),
      ]);
      setTournament(tournamentData);
      setStages(stagesData);
    } catch (error) {
      console.error('Erro ao carregar torneio:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center text-white">
        <Trophy className="w-16 h-16 text-gray-600 mb-4" />
        <h2 className="text-2xl font-bold mb-2">Torneio não encontrado</h2>
        <Link to="/torneios" className="text-amber-400 hover:underline">
          Voltar para lista de torneios
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="relative">
        {/* Banner */}
        <div className="h-64 bg-gradient-to-br from-amber-600 to-orange-700">
          {tournament.bannerUrl && (
            <img
              src={tournament.bannerUrl}
              alt={tournament.name}
              className="w-full h-full object-cover opacity-50"
            />
          )}
        </div>

        {/* Back Button */}
        <Link
          to="/torneios"
          className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm rounded-full p-2 hover:bg-black/70 transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </Link>

        {/* Tournament Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-gray-900 via-gray-900/80 to-transparent p-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-2">
              {tournament.type === 'official' && (
                <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  OFICIAL CBPE
                </span>
              )}
              <span className="bg-amber-500/20 text-amber-400 text-xs font-bold px-2 py-1 rounded-full">
                Temporada {tournament.season}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold">{tournament.name}</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Description */}
        {tournament.description && (
          <div className="bg-gray-800 rounded-xl p-6 mb-6">
            <h2 className="text-xl font-bold mb-3">Sobre o Torneio</h2>
            <p className="text-gray-300 whitespace-pre-line">{tournament.description}</p>
          </div>
        )}

        {/* Rules Link */}
        {tournament.rulesUrl && (
          <a
            href={tournament.rulesUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-gray-800 hover:bg-gray-750 rounded-xl p-4 mb-6 transition-colors"
          >
            <ExternalLink className="w-5 h-5 text-amber-400" />
            <span className="font-medium">Ver Regulamento Completo</span>
          </a>
        )}

        {/* Stages */}
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-amber-400" />
            Etapas ({stages.length})
          </h2>

          {stages.length === 0 ? (
            <div className="bg-gray-800 rounded-xl p-8 text-center">
              <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">Nenhuma etapa cadastrada ainda.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {stages.map((stage, index) => (
                <StageCard key={stage.id} stage={stage} index={index + 1} />
              ))}
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-xl p-6 text-center">
          <h3 className="text-2xl font-bold mb-2">Pronto para competir?</h3>
          <p className="text-amber-100 mb-4">
            Selecione uma etapa acima para se inscrever
          </p>
        </div>
      </div>
    </div>
  );
};

const StageCard: React.FC<{ stage: TournamentStage; index: number }> = ({ stage, index }) => {
  const now = new Date();
  const registrationOpen = stage.dates.registrationDeadline > now;
  const isVirtual = stage.isVirtual;

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  return (
    <Link
      to={`/torneios/etapa/${stage.id}`}
      className="block bg-gray-800 rounded-xl p-5 hover:bg-gray-750 transition-all group"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="bg-amber-500/20 text-amber-400 text-xs font-bold px-2 py-0.5 rounded">
              ETAPA {index}
            </span>
            {isVirtual && (
              <span className="bg-purple-500/20 text-purple-400 text-xs font-bold px-2 py-0.5 rounded">
                VIRTUAL
              </span>
            )}
          </div>

          <h3 className="text-lg font-bold group-hover:text-amber-400 transition-colors">
            {stage.name}
          </h3>

          <div className="mt-3 space-y-1 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>{stage.location.name}, {stage.location.city} - {stage.location.state}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(stage.dates.start)} - {formatDate(stage.dates.end)}</span>
            </div>
          </div>
        </div>

        <div className="text-right">
          {registrationOpen ? (
            <div className="flex items-center gap-1 text-green-400 text-sm">
              <CheckCircle className="w-4 h-4" />
              <span>Inscrições Abertas</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-gray-500 text-sm">
              <Clock className="w-4 h-4" />
              <span>Encerrado</span>
            </div>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Até {formatDate(stage.dates.registrationDeadline)}
          </p>
        </div>
      </div>
    </Link>
  );
};

export default TournamentDetailPage;
